/**
 * Guest portal information architecture — five sections, in the order a
 * guest actually moves through them. Every guest-facing route belongs to
 * exactly one section so the shell can always highlight where you are.
 */
import {
  Activity, HeartPulse, Stethoscope, FolderHeart, Wallet,
} from "lucide-react";

export const GUEST_SECTIONS = [
  {
    id: "now",
    label: "Now",
    icon: Activity,
    to: "/guest",
    tid: "guest-nav-now",
    blurb: "What needs you",
    children: [],
  },
  {
    id: "get-care",
    label: "Get care",
    icon: HeartPulse,
    to: "/guest/care",
    tid: "guest-nav-get-care",
    blurb: "See a doctor",
    children: [
      { to: "/guest/care", label: "Ways to get care", tid: "guest-tab-care-hub" },
      { to: "/triage", label: "Describe symptoms", tid: "guest-tab-triage" },
      { to: "/doctors", label: "Choose a doctor", tid: "guest-tab-doctors" },
      { to: "/care/finder", label: "Pharmacies & labs", tid: "guest-tab-finder" },
    ],
  },
  {
    id: "my-care",
    label: "My care",
    icon: Stethoscope,
    to: "/guest/my-care",
    tid: "guest-nav-my-care",
    blurb: "Visits, meds and tests",
    badge: "care",
    children: [
      { to: "/guest/my-care", label: "Timeline", tid: "guest-tab-timeline" },
      { to: "/guest/consultations", label: "Visits", tid: "guest-tab-visits" },
      { to: "/guest/prescriptions", label: "Prescriptions", tid: "guest-tab-rx" },
      { to: "/guest/labs", label: "Test results", tid: "guest-tab-labs" },
      { to: "/fulfilment", label: "Orders & delivery", tid: "guest-tab-orders", badge: "partner" },
      { to: "/guest/messages", label: "Messages", tid: "guest-tab-messages", badge: "clinical" },
    ],
  },
  {
    id: "health-file",
    label: "Health file",
    icon: FolderHeart,
    to: "/guest/records",
    tid: "guest-nav-health-file",
    blurb: "Records and family",
    children: [
      { to: "/guest/records", label: "Health record", tid: "guest-tab-records" },
      { to: "/guest/intake", label: "Pre-visit form", tid: "guest-tab-intake" },
      { to: "/dependants", label: "Family", tid: "guest-tab-family" },
    ],
  },
  {
    id: "account",
    label: "Account",
    icon: Wallet,
    to: "/guest/account",
    tid: "guest-nav-account",
    blurb: "Payments and privacy",
    children: [
      { to: "/guest/account", label: "Overview", tid: "guest-tab-account" },
      { to: "/guest/billing", label: "Payments", tid: "guest-tab-billing" },
      { to: "/settings/notifications", label: "Alerts", tid: "guest-tab-alerts" },
      { to: "/settings/consent", label: "Consent", tid: "guest-tab-consent" },
      { to: "/settings/privacy", label: "Privacy", tid: "guest-tab-privacy" },
    ],
  },
];

/** Longest-prefix match so /doctors and /guest/labs both resolve correctly. */
export function sectionForPath(pathname) {
  let best = null;
  let bestLen = -1;
  for (const s of GUEST_SECTIONS) {
    for (const p of [s.to, ...s.children.map((c) => c.to)]) {
      const exact = pathname === p;
      const nested = p !== "/guest" && pathname.startsWith(`${p}/`);
      if ((exact || nested) && p.length > bestLen) {
        best = s;
        bestLen = p.length;
      }
    }
  }
  // Flows that belong to a section but have their own deep routes.
  if (!best) {
    if (/^\/(book|care)\//.test(pathname) || pathname.startsWith("/triage")) {
      best = GUEST_SECTIONS[1];
    } else if (pathname.startsWith("/payment")) {
      best = GUEST_SECTIONS[4];
    }
  }
  return best;
}

/** Every path that should render inside the guest shell. */
export function isGuestShellPath(pathname) {
  if (/^\/guest\/consult\//.test(pathname)) return false;   // immersive video
  if (/^\/care\/[^/]+$/.test(pathname) && pathname !== "/care/finder") return false;
  if (pathname.startsWith("/payment/")) return false;        // checkout takes over
  return Boolean(sectionForPath(pathname));
}
